<template>
  <div>
    <b-modal
      modal-class="modal-primary aprobar-bono"
      size="xmd"
      hide-footer
      no-close-on-backdrop
      no-close-on-esc
      v-model="show"
      @hidden="$emit('close')"
    >
      <!-- CABECERA: a quien y por que periodo se le paga -->
      <template #modal-title>
        <div class="d-flex flex-column">
          <span class="ab-title">Aprobar bono</span>
          <span class="ab-subtitle">
            {{ modalData.nombre_completo }} · {{ modalData.numero_documento }}
          </span>
        </div>
      </template>

      <div class="ab-header">
        <div class="ab-header__chips">
          <b-badge :variant="estadoVariant" class="text-uppercase">
            {{ estado }}
          </b-badge>
          <b-badge variant="light-secondary" v-if="modalData.bono_categoria">
            Categoría {{ modalData.bono_categoria }}
          </b-badge>
          <b-badge variant="light-secondary" v-if="modalData.tipo_ut">
            UT {{ modalData.tipo_ut }}
          </b-badge>
        </div>
        <div class="ab-header__periodo">
          <feather-icon icon="CalendarIcon" size="14" class="mr-50" />
          <span>{{ modalData.periodo }}</span>
          <span class="text-muted ml-50">
            {{ modalData.fecha_inicio | globalDate }} —
            {{ modalData.fecha_fin | globalDate }}
          </span>
        </div>
      </div>

      <!-- QUE TIENE QUE HACER EL USUARIO -->
      <b-alert :variant="guia.variant" show class="ab-guia">
        <div class="d-flex">
          <feather-icon :icon="guia.icon" size="18" class="mr-1 mt-25" />
          <div>
            <div class="font-weight-bolder">{{ guia.titulo }}</div>
            <div class="ab-guia__pasos">{{ guia.detalle }}</div>
          </div>
        </div>
      </b-alert>

      <!-- RESUMEN: solo los conceptos que suman monto -->
      <b-card class="ab-card" body-class="p-1">
        <div class="ab-card__head">
          <h6 class="ab-card__title">RESUMEN DEL BONO</h6>
          <small class="text-muted">Solo se listan los conceptos con monto</small>
        </div>

        <div v-if="conceptos.length" class="ab-conceptos">
          <div v-for="c in conceptos" :key="c.key" class="ab-concepto">
            <div class="ab-concepto__label">
              <span>{{ c.label }}</span>
              <b-badge variant="light-secondary" v-if="c.cantidad">
                {{ c.cantidad }} {{ c.cantidad == 1 ? "viaje" : "viajes" }}
              </b-badge>
            </div>
            <span class="ab-concepto__monto">S/ {{ c.monto | currency }}</span>
          </div>
        </div>

        <div v-else class="ab-vacio">
          Este bono no tiene ningún concepto con monto. Revisa las observaciones
          antes de aprobarlo.
        </div>

        <div class="ab-totales">
          <div class="ab-total">
            <span>Subtotal</span>
            <span>S/ {{ numero(bono.total_bono_bruto) | currency }}</span>
          </div>
          <div class="ab-total">
            <span>Adicionales</span>
            <span class="text-success">
              + S/ {{ numero(bono.adicional_bono) | currency }}
            </span>
          </div>
          <div class="ab-total">
            <span>Descuentos</span>
            <span class="text-danger">
              − S/ {{ numero(bono.descuentos) | currency }}
            </span>
          </div>
          <div class="ab-total ab-total--final">
            <span>Total a pagar</span>
            <span>S/ {{ numero(bono.total_bono_neto) | currency }}</span>
          </div>
        </div>
      </b-card>

      <!-- DESCUENTOS -->
      <b-card class="ab-card" body-class="p-1">
        <div class="ab-card__head">
          <h6 class="ab-card__title">DESCUENTOS POR CONCEPTO</h6>
          <small class="text-muted">
            Marca la casilla para aplicar un descuento a este bono
          </small>
        </div>

        <b-table-simple
          v-if="bono.discounts && bono.discounts.length"
          hover
          small
          responsive
          class="font-small-3 mb-0"
          table-class="text-nowrap"
          sticky-header="220px"
        >
          <b-thead head-variant="light">
            <b-tr>
              <b-th>{{ $t("Registered At") }}</b-th>
              <b-th>{{ $t("Discount Date") }}</b-th>
              <b-th>{{ $t("Type") }}</b-th>
              <b-th class="text-right">{{ $t("Amount") }}</b-th>
              <b-th class="text-center">{{ $t("Status") }}</b-th>
              <b-th class="text-center">Aplicar</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(disc, key) in bono.discounts" :key="key">
              <b-td>{{ disc.date | globalDate }}</b-td>
              <b-td>{{ disc.discount_date | globalDate }}</b-td>
              <b-td>{{ disc.discount_type_name }}</b-td>
              <b-td class="text-right text-danger font-weight-bolder">
                S/ {{ disc.amount | currency }}
              </b-td>
              <b-td class="text-center">
                <b-badge
                  :variant="
                    disc.status_fraction == 'Discounted'
                      ? 'light-success'
                      : 'light-warning'
                  "
                >
                  {{ disc.status_fraction }}
                </b-badge>
              </b-td>
              <b-td class="text-center">
                <b-form-checkbox
                  v-model="disc.check"
                  :disabled="disc.status_fraction == 'Discounted'"
                  :value="1"
                  class="custom-control-primary d-inline-block"
                  @change="addDiscountToPayroll(disc)"
                />
              </b-td>
            </b-tr>
          </b-tbody>
          <b-tfoot>
            <b-tr>
              <b-td colspan="3" class="text-right text-muted">
                {{ $t("Total Discount") }}:
              </b-td>
              <b-td colspan="3">
                <b-badge variant="light-danger">
                  S/ {{ getTotalDiscount | currency }}
                </b-badge>
              </b-td>
            </b-tr>
          </b-tfoot>
        </b-table-simple>

        <div v-else class="ab-vacio">
          Este trabajador no tiene descuentos pendientes.
        </div>
      </b-card>

      <!-- ADICIONALES -->
      <b-card class="ab-card" body-class="p-1">
        <div class="ab-card__head">
          <div>
            <h6 class="ab-card__title">ADICIONALES</h6>
            <small class="text-muted">
              Montos extra que se suman al bono de este periodo
            </small>
          </div>
          <b-button
            size="sm"
            variant="outline-success"
            @click="agregarAdicional(bono)"
          >
            <feather-icon icon="PlusIcon" size="14" class="mr-25" />
            Agregar
          </b-button>
        </div>

        <b-table-simple
          v-if="bono.adicionales && bono.adicionales.length"
          hover
          small
          responsive
          class="font-small-3 mb-0"
          table-class="text-nowrap"
          sticky-header="220px"
        >
          <b-thead head-variant="light">
            <b-tr>
              <b-th>Responsable</b-th>
              <b-th class="text-center">Fecha registro</b-th>
              <b-th class="text-right">Monto</b-th>
              <b-th>Comentario</b-th>
              <b-th class="text-center">Acciones</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(adic, key) in bono.adicionales" :key="key">
              <b-td>{{ adic.created_by }}</b-td>
              <b-td class="text-center">{{ adic.created_at | globalDate }}</b-td>
              <b-td class="text-right text-success font-weight-bolder">
                S/ {{ adic.monto | currency }}
              </b-td>
              <b-td class="ab-comentario">{{ adic.comentario }}</b-td>
              <b-td class="text-center">
                <feather-icon
                  icon="TrashIcon"
                  class="text-danger cursor-pointer"
                  v-b-tooltip.hover.top="'Eliminar adicional'"
                  @click="eliminarAdicional(adic.id)"
                />
              </b-td>
            </b-tr>
          </b-tbody>
          <b-tfoot>
            <b-tr>
              <b-td colspan="2" class="text-right text-muted">
                Total adicionales:
              </b-td>
              <b-td colspan="3">
                <b-badge variant="light-success">
                  S/ {{ getTotalAdicional | currency }}
                </b-badge>
              </b-td>
            </b-tr>
          </b-tfoot>
        </b-table-simple>

        <div v-else class="ab-vacio">
          Sin adicionales. Usa "Agregar" si corresponde sumar un monto extra.
        </div>
      </b-card>

      <!-- ACCIONES -->
      <div class="ab-footer">
        <div class="ab-footer__total">
          <small class="text-muted">Se generará una caja por</small>
          <span>S/ {{ numero(bono.total_bono_neto) | currency }}</span>
        </div>
        <div class="ab-footer__acciones">
          <b-button variant="outline-secondary" @click="$emit('close')">
            Cerrar
          </b-button>
          <span v-b-tooltip.hover.top="motivoBloqueo">
            <b-button
              variant="primary"
              :disabled="!puedeAprobar"
              @click="approvePayroll"
            >
              <feather-icon icon="CheckCircleIcon" size="16" class="mr-50" />
              Aprobar bono
            </b-button>
          </span>
        </div>
      </div>
    </b-modal>

    <BonoConfirmacionModal
      v-if="showConfirmacionBonoModal"
      :idBonos="idBonosToApprove"
      @close="showConfirmacionBonoModal = false"
      @confirmed="handleConfirmacionAprobacionBono"
    />

    <AdicionalBonoModal
      v-if="showAdicionalBonoModal"
      :modalData="dataBono"
      @refresh="refreshAdicionalBono"
      @close="showAdicionalBonoModal = false"
      @hideModal="showAdicionalBonoModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BonoService from "@/views/rrhh/views/payroll/components/bonos/service/bono.service.js";
import BonoConfirmacionModal from "@/views/rrhh/views/payroll/components/bonos/components/BonoConfirmacionModal.vue";
import AdicionalBonoModal from "@/views/rrhh/views/payroll/components/bonos/components/AdicionalBonoModal.vue";

// Conceptos que puede pagar un bono, en el mismo orden y con los mismos nombres
// que la tabla de bonos. En el resumen solo se muestran los que tienen monto.
const CONCEPTOS = [
  { key: "tramo_1", label: "Viajes 0 - 100 km", campo: "mas21_tramo_1" },
  { key: "tramo_2", label: "Viajes 100 - 250 km", campo: "mas21_tramo_2" },
  { key: "tramo_3", label: "Viajes 250 - 980 km", campo: "mas21_tramo_3" },
  { key: "tramo_4", label: "Viajes 980 - 1400 km", campo: "mas21_tramo_4" },
  { key: "tramo_5", label: "Viajes 1400 - 1800 km", campo: "mas21_tramo_5" },
  { key: "f1", label: "Bonos F1 (transferencias)", campo: "bono_f1" },
  { key: "cambio", label: "Cambios de conductor", campo: "cambio_conductor" },
  { key: "retornos", label: "Viajes de retorno", campo: "retornos" },
  { key: "devoluciones", label: "Viajes con devolución", campo: "devoluciones" },
];

export default {
  components: {
    BonoConfirmacionModal,
    AdicionalBonoModal,
  },
  props: {
    modalData: {
      type: Object,
      require: true,
    },
  },
  data() {
    return {
      show: true,
      bono: {
        discounts: [],
        adicionales: [],
      },
      dataBono: null,
      showAdicionalBonoModal: false,
      showConfirmacionBonoModal: false,
    };
  },
  async mounted() {
    await this.getBono();
  },
  computed: {
    ...mapGetters({
      currentUser: "auth/currentUser",
    }),
    estado() {
      return this.bono.estado || this.modalData.estado || "";
    },
    estadoVariant() {
      return (
        {
          FINALIZADO: "light-success",
          OBSERVADO: "light-warning",
          APROBADO: "light-info",
          PROCESANDO: "light-secondary",
          RECHAZADO: "light-danger",
          ANULADO: "light-danger",
        }[this.estado] || "light-secondary"
      );
    },
    conceptos() {
      return CONCEPTOS.map((c) => ({
        key: c.key,
        label: c.label,
        cantidad: this.numero(this.bono[`${c.campo}_cantidad`]),
        monto: this.numero(this.bono[`${c.campo}_monto`]),
      })).filter((c) => c.monto > 0);
    },
    descuentosPendientes() {
      return (this.bono.discounts || []).filter((d) => !d.check).length;
    },
    puedeAprobar() {
      return (
        ["FINALIZADO", "OBSERVADO"].includes(this.estado) &&
        this.numero(this.bono.total_bono_neto) > 0
      );
    },
    motivoBloqueo() {
      if (this.puedeAprobar) return "";
      if (this.estado === "APROBADO")
        return "Este bono ya fue aprobado. Aprobarlo otra vez generaría una segunda caja.";
      if (this.estado === "PROCESANDO")
        return "El bono todavía se está calculando. Espera a que termine.";
      if (this.numero(this.bono.total_bono_neto) <= 0)
        return "El total a pagar es S/ 0.00, no hay nada que aprobar.";
      return `Un bono en estado ${this.estado} no se puede aprobar.`;
    },
    // Le dice al usuario, segun el estado del bono, que tiene que hacer aqui.
    guia() {
      if (this.estado === "APROBADO") {
        return {
          variant: "danger",
          icon: "AlertOctagonIcon",
          titulo: "Este bono ya fue aprobado",
          detalle:
            "Su caja ya fue generada. Si necesitas corregirlo, comunícate con el equipo de desarrollo: volver a aprobarlo generaría un segundo pago.",
        };
      }

      if (this.estado === "PROCESANDO") {
        return {
          variant: "secondary",
          icon: "ClockIcon",
          titulo: "El bono se está calculando",
          detalle: "Cierra esta ventana y vuelve a entrar en unos minutos.",
        };
      }

      if (this.estado === "OBSERVADO") {
        return {
          variant: "warning",
          icon: "AlertTriangleIcon",
          titulo: "Este bono tiene viajes observados",
          detalle:
            "Hay viajes que no se pudieron calcular, así que el monto podría estar incompleto. Revisa las observaciones en la tabla (ícono rojo) antes de aprobar.",
        };
      }

      return {
        variant: "primary",
        icon: "InfoIcon",
        titulo: "Revisa antes de aprobar",
        detalle: this.descuentosPendientes
          ? `1) Verifica el resumen del bono. 2) Tienes ${this.descuentosPendientes} descuento(s) sin aplicar: marca los que correspondan. 3) Pulsa "Aprobar bono" y confirma los datos de la caja.`
          : '1) Verifica el resumen del bono y sus descuentos. 2) Pulsa "Aprobar bono" y confirma los datos de la caja.',
      };
    },
    getTotalDiscount() {
      return (this.bono.discounts || []).reduce(
        (total, dis) => total + this.numero(dis.amount),
        0
      );
    },
    getTotalAdicional() {
      return (this.bono.adicionales || []).reduce(
        (total, adic) => total + this.numero(adic.monto),
        0
      );
    },
    idBonosToApprove() {
      return [
        {
          user_id: this.currentUser.id,
          bono_id: this.modalData.id,
        },
      ];
    },
  },
  methods: {
    numero(valor) {
      return Number(valor) || 0;
    },
    async approvePayroll() {
      this.showConfirmacionBonoModal = true;
    },
    handleConfirmacionAprobacionBono() {
      this.$emit("approvePayroll", {
        user_id: this.currentUser.id,
        bono_id: this.modalData.id,
      });
    },
    async eliminarAdicional(id) {
      try {
        const confirm = await this.showConfirmSwal();
        if (!confirm.isConfirmed) return;

        this.addPreloader();
        const { data } = await BonoService.eliminarAdicionalBono({
          id,
          detalle_bono_id: id,
        });
        this.showToastSuccess(data.message);
        await this.getBono();
      } catch (error) {
        console.log(error);
        const errormessage = error.response?.data?.message || "Ocurrio un error";
        this.showToastError(errormessage);
      } finally {
        this.removePreloader();
      }
    },
    agregarAdicional() {
      this.showAdicionalBonoModal = true;
      this.dataBono = {
        id: null,
        detalle_bono_id: this.modalData.id,
        personal: this.modalData.nombre_completo,
        periodo: this.modalData.periodo,
        fecha_inicio: this.modalData.fecha_inicio,
        fecha_fin: this.modalData.fecha_fin,
      };
    },
    async refreshAdicionalBono() {
      this.showAdicionalBonoModal = false;
      await this.getBono();
    },
    async getBono() {
      try {
        const { data } = await BonoService.getBono({
          bono_id: this.modalData.id,
        });
        this.bono = data.data || { discounts: [], adicionales: [] };
      } catch (error) {
        console.log(error);
      }
    },
    async addDiscountToPayroll(discount) {
      const check = discount.check;
      const confirm = await this.showConfirmSwal();
      if (confirm.isConfirmed) {
        try {
          this.addPreloader();
          const params = {
            bono_id: this.modalData.id,
            discount_fraction_id: discount.fraction_id,
            check,
          };
          await BonoService.addDiscountToBono(params);
          this.showToastSuccess(
            check ? "Descuento aplicado" : "Descuento eliminado"
          );
          await this.getBono();
        } catch (error) {
          console.log("error; ", error);
          const errormessage = error.response?.data?.message || "Ocurrio un error";
          this.$set(discount, "check", check ? 0 : 1);
          this.showToastError(errormessage);
        } finally {
          this.removePreloader();
        }
      } else {
        this.$set(discount, "check", check ? 0 : 1);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ab-title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.2;
}

.ab-subtitle {
  font-size: 0.8rem;
  opacity: 0.85;
}

.ab-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  &__periodo {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    font-size: 0.85rem;
  }
}

.ab-guia {
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;

  &__pasos {
    font-size: 0.82rem;
    line-height: 1.45;
  }
}

.ab-card {
  margin-bottom: 1rem;
  border: 1px solid rgba(34, 41, 47, 0.1);
  box-shadow: none;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  &__title {
    margin-bottom: 0.1rem;
    color: #7367f0;
    font-weight: 600;
    letter-spacing: 0.02em;
  }
}

.ab-conceptos {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.35rem 1.25rem;
}

.ab-concepto {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0;
  border-bottom: 1px dashed rgba(34, 41, 47, 0.12);

  &__label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.85rem;
  }

  &__monto {
    font-weight: 600;
    white-space: nowrap;
  }
}

.ab-totales {
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(34, 41, 47, 0.1);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
}

.ab-total {
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;

  span:last-child {
    font-weight: 600;
  }

  &--final {
    span:first-child {
      font-weight: 600;
    }

    span:last-child {
      font-size: 1.15rem;
      color: #7367f0;
    }
  }
}

.ab-vacio {
  padding: 1rem;
  text-align: center;
  font-size: 0.85rem;
  color: #6e6b7b;
  background: rgba(34, 41, 47, 0.03);
  border-radius: 0.4rem;
}

.ab-comentario {
  white-space: normal;
  max-width: 320px;
}

.ab-footer {
  position: sticky;
  bottom: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(34, 41, 47, 0.1);
  background: inherit;

  &__total {
    display: flex;
    flex-direction: column;
    line-height: 1.2;

    span {
      font-size: 1.2rem;
      font-weight: 600;
    }
  }

  &__acciones {
    display: flex;
    gap: 0.75rem;
  }
}

.dark-layout {
  .ab-card {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .ab-concepto {
    border-bottom-color: rgba(255, 255, 255, 0.12);
  }

  .ab-totales,
  .ab-footer {
    border-top-color: rgba(255, 255, 255, 0.1);
  }

  .ab-vacio {
    color: #b4b7bd;
    background: rgba(255, 255, 255, 0.05);
  }
}
</style>
